/**
 * GpuTimerFactory — selects the appropriate IGpuTimerBackend based on
 * detected runtime capabilities.
 *
 * Selection priority:
 *  1. WebGPU + timestamp-query feature  → WebGPUGpuTimer
 *  2. WebGL2 + EXT_disjoint_timer_query_webgl2 → WebGL2GpuTimer
 *  3. Neither → NullGpuTimer
 *
 * The selection is logged once at Info level.
 */
import type { IGpuTimerBackend } from './GpuTimerBackend.js';
import { NullGpuTimer } from './NullGpuTimer.js';
import { WebGPUGpuTimer } from './WebGPUGpuTimer.js';
import { WebGL2GpuTimer } from './WebGL2GpuTimer.js';
import { Logger } from '../../core/Logger.js';

/**
 * Minimal renderer interface needed for backend detection.
 * Matches EngineRenderer without creating a direct dependency.
 */
export interface GpuTimerRendererLike {
  /** True when the renderer is using the WebGPU backend. */
  isWebGPU: boolean;
  /** The underlying Three.js renderer (WebGPURenderer or WebGLRenderer). */
  native: {
    backend?: {
      device?: unknown;
    };
    getContext?(): unknown;
  };
}

/** Options forwarded to the selected backend. */
export interface GpuTimerOptions {
  /** Maximum number of named passes per frame (default 64). */
  maxPasses?: number;
}

/**
 * Create the best available GPU timer backend for the given renderer.
 *
 * @param renderer  An EngineRenderer-like object exposing isWebGPU and native.
 * @param options   Optional configuration forwarded to the backend.
 */
export function createGpuTimer(
  renderer: GpuTimerRendererLike,
  options: GpuTimerOptions = {},
): IGpuTimerBackend {
  const maxPasses = options.maxPasses ?? 64;

  // --- WebGPU path ---
  if (renderer.isWebGPU) {
    const device = renderer.native.backend?.device as unknown;
    if (device !== null && device !== undefined) {
      const timer = new WebGPUGpuTimer(device, maxPasses);
      if (timer.available) {
        Logger.info('GpuTimerFactory', 'Selected WebGPUGpuTimer (timestamp-query)');
        return timer;
      }
      timer.dispose();
    }
  }

  // --- WebGL2 path ---
  if (!renderer.isWebGPU) {
    const gl = typeof renderer.native.getContext === 'function'
      ? renderer.native.getContext()
      : null;

    if (gl !== null && gl !== undefined) {
      const timer = new WebGL2GpuTimer(gl, maxPasses);
      if (timer.available) {
        Logger.info('GpuTimerFactory', 'Selected WebGL2GpuTimer (EXT_disjoint_timer_query_webgl2)');
        return timer;
      }
      timer.dispose();
    }
  }

  // --- Fallback ---
  Logger.info('GpuTimerFactory', 'Selected NullGpuTimer (no GPU timing available)');
  return new NullGpuTimer();
}
